import { useMemo } from 'react'

import { type EntityDescriptor, checkEntityAccess } from '@/utils/access-control/perEntity'
import { usePermissions } from '@/utils/permissions'

export type EntityAccessResult = { allowed: true } | { allowed: false; reason: 'no-entity-permission' }

/**
 * Per-entity RBAC primitive.
 *
 * Reads the current platform user's permissions and runs `checkEntityAccess`
 * against the supplied descriptor.
 *
 * Returns `{ allowed: true }` when no `entity` is supplied — keeps the call
 * site simple for components that conditionally check per-entity access.
 */
export function useEntityAccess(entity?: EntityDescriptor): EntityAccessResult {
	const permissions = usePermissions()

	return useMemo((): EntityAccessResult => {
		if (!entity) {
			return { allowed: true }
		}

		return checkEntityAccess(permissions, entity)
			? { allowed: true }
			: { allowed: false, reason: 'no-entity-permission' }
	}, [entity, permissions])
}
